import type { Day } from '../core/calendar.ts';
import { add, dec, div, fromDouble, gt, isZero, min, mul, round2, ZERO, type Decimal } from '../core/decimal.ts';
import { timeBuckets } from '../forecast/window.ts';
import { annualOccurrences, frequencyStep, monthlyEquivalent, scheduleDate } from '../models/frequency.ts';
import { amountOn, fieldsOn, paidOn, percentMultiplier } from '../models/transaction.ts';
import type { Bucket, FrequencyUnit, Transaction, Window } from '../types/domain.ts';
import { calculatePayments } from './payment-calculator.ts';
import { runPot } from './pot.ts';

const DEFAULT_ANNUAL_RETURN_PERCENT = dec(6);
const DEFAULT_WITHDRAWAL_RATE_PERCENT = dec(4);

// MARK: Employer match (R13)

/** An income pays on `date` when it is active, recurring, started and not past its end date or last occurrence. */
function isPaying(income: Transaction, date: Day): boolean {
  const step = frequencyStep(income.frequency);
  if (income.type !== 'income' || !income.isActive || !(income.startDate <= date) || step === null || step.count <= 0) return false;
  if (income.endDate !== null && date > income.endDate) return false;
  if (income.endAfterOccurrences !== null) {
    return income.endAfterOccurrences > 0 && date <= scheduleDate(income.startDate, step, income.endAfterOccurrences - 1);
  }
  return true;
}

/**
 * EmployerMatch.monthlySalary: the monthly equivalent of every active recurring income paying on
 * `date`, each at its R11 amount.
 */
export const monthlySalary = (incomes: readonly Transaction[], date: Day): Decimal =>
  incomes.reduce<Decimal>(
    (total, income) => (isPaying(income, date) ? add(total, monthlyEquivalent(income.frequency, amountOn(income, date))) : total),
    ZERO,
  );

/**
 * The income an employer match follows on `date` (decision 5): the linked one while it is paying,
 * else, with no link, the largest paying recurring income (the first listed wins a tie).
 */
export function matchedIncome(investment: Transaction, incomes: readonly Transaction[], date: Day): Transaction | null {
  const paying = incomes.filter((income) => isPaying(income, date));
  if (investment.employerMatchIncomeId !== null) return paying.find((income) => income.id === investment.employerMatchIncomeId) ?? null;
  const monthly = (income: Transaction) => monthlyEquivalent(income.frequency, amountOn(income, date));
  return paying.reduce<Transaction | null>((best, income) => (best === null || gt(monthly(income), monthly(best)) ? income : best), null);
}

/**
 * EmployerMatch.contribution (TIMELINE R23, decision 5): with each positive recurring contribution
 * of `own`, employerMatch percent of the matched income's monthly pay scaled to the contribution's
 * period (× 12 / annualOccurrences), rounded 2dp, up to `own` itself; nothing on a £0 or one-off
 * contribution or without a paying income.
 */
export function employerMatchContribution(
  investment: Transaction,
  incomes: readonly Transaction[],
  date: Day,
  own: Decimal = amountOn(investment, date),
): Decimal {
  const percent = fieldsOn(investment, date).employerMatch;
  const step = frequencyStep(investment.frequency);
  if (percent === null || isZero(percent) || step === null || step.count <= 0 || !gt(own, ZERO)) return ZERO;
  const income = matchedIncome(investment, incomes, date);
  if (income === null) return ZERO;
  const pay = monthlyEquivalent(income.frequency, amountOn(income, date));
  const perPeriod = div(mul(div(mul(pay, percent), dec(100)), dec(12)), fromDouble(annualOccurrences(investment.frequency)));
  return min(own, round2(perPeriod));
}

/**
 * InvestmentProjector.calculateTotalInvestment (R13, R23): what one contribution on `date` (default
 * the start date) puts into the pot, for any type: the saver's own R11 amount grossed up by tax
 * relief, rounded 2dp, plus the employer's match. Only the saver's own amount comes out of cash.
 */
export function calculateTotalInvestment(
  transaction: Transaction,
  date: Day = transaction.startDate,
  incomes: readonly Transaction[] = [],
): Decimal {
  const own = paidOn(transaction, date);
  const personal = round2(mul(own, percentMultiplier(transaction.taxRelief?.reliefPercentage ?? dec(0))));
  return add(personal, employerMatchContribution(transaction, incomes, date, own));
}

const isActiveInvestment = (transaction: Transaction): boolean => transaction.type === 'investment' && transaction.isActive;

// MARK: Single-transaction forecast

export interface InvestmentInterestForecast {
  /** Balance at each bucket before that bucket's contribution, unrounded. */
  readonly transactionForecast: readonly Decimal[];
  readonly interestForecast: readonly Decimal[];
  readonly interestTotal: Decimal;
  readonly total: Decimal;
}

/** getIntervalAdjustedInterestRate: the annual percentage split per bucket interval. */
function intervalRatePercent(annualRate: Decimal, interval: FrequencyUnit): Decimal {
  switch (interval) {
    case 'day':
      return div(annualRate, dec(365));
    case 'week':
      return div(annualRate, dec(52));
    case 'month':
      return div(annualRate, dec(12));
    case 'year':
      return annualRate;
  }
}

/**
 * InvestmentProjector.createInvestmentTransactionForecast, at the transaction's own rate or else
 * `annualReturnPercent` (R12). Bucket 0 is the initial balance;
 * each later bucket grows the previous balance by one interval's rate and then adds the
 * previous bucket's contribution: `b += interest + contribution[i-1]` (grouped as Swift does).
 */
export function createInvestmentTransactionForecast(
  transaction: Transaction,
  window: Window,
  annualReturnPercent: Decimal = DEFAULT_ANNUAL_RETURN_PERCENT,
  incomes: readonly Transaction[] = [],
): InvestmentInterestForecast {
  const ratePercent = intervalRatePercent(transaction.interestRate ?? annualReturnPercent, window.interval);
  const rate = div(ratePercent, dec(100));
  const contributions = calculatePayments([transaction], window, () => true, (t, date) => calculateTotalInvestment(t, date, incomes)).map(
    (bucket) => bucket.amount,
  );

  const balances: Decimal[] = [];
  const interests: Decimal[] = [];
  let balance = transaction.initialBalance ?? ZERO;
  let interestTotal: Decimal = ZERO;
  contributions.forEach((_, i) => {
    const interest = i === 0 ? ZERO : mul(balance, rate);
    if (i > 0) {
      interestTotal = add(interestTotal, interest);
      balance = add(balance, add(interest, contributions[i - 1]!));
    }
    balances.push(balance);
    interests.push(interest);
  });

  return {
    transactionForecast: balances,
    interestForecast: interests,
    interestTotal: round2(interestTotal),
    total: round2(balance),
  };
}

// MARK: Portfolio projection

export interface InvestmentBucket extends Bucket {
  readonly passiveIncome: Decimal;
}

/**
 * InvestmentProjector.project: per bucket, round2 of the active investments' balances summed
 * left to right from 0, and passive income round2(amount × withdrawalRate/100).
 */
export function projectInvestments(
  transactions: readonly Transaction[],
  window: Window,
  withdrawalRatePercent: Decimal = DEFAULT_WITHDRAWAL_RATE_PERCENT,
  annualReturnPercent: Decimal = DEFAULT_ANNUAL_RETURN_PERCENT,
  incomes: readonly Transaction[] = [],
): readonly InvestmentBucket[] {
  const series = transactions
    .filter(isActiveInvestment)
    .map((transaction) => createInvestmentTransactionForecast(transaction, window, annualReturnPercent, incomes).transactionForecast);
  const withdrawalRate = div(withdrawalRatePercent, dec(100));
  return timeBuckets(window).map((bucket, i) => {
    const amount = round2(series.reduce<Decimal>((total, balances) => add(total, balances[i] ?? ZERO), ZERO));
    return { ...bucket, amount, passiveIncome: round2(mul(amount, withdrawalRate)) };
  });
}

// MARK: Current value

/**
 * InvestmentProjector.calculateCurrentValue (TIMELINE R23): the pot at the end of `date` on the
 * ledger's rules (`runPot`), rounded to 2dp; 0 before it opens.
 */
export function calculateCurrentValue(
  investment: Transaction,
  date: Day,
  annualReturnPercent: Decimal = DEFAULT_ANNUAL_RETURN_PERCENT,
  incomes: readonly Transaction[] = [],
): Decimal {
  return round2(runPot(investment, incomes, annualReturnPercent, [date]).values[0]!);
}

/** InvestmentProjector.calculateTotalCurrentInvestments: round2(Σ current value of active investments). */
export const calculateTotalCurrentInvestments = (
  transactions: readonly Transaction[],
  date: Day,
  annualReturnPercent: Decimal = DEFAULT_ANNUAL_RETURN_PERCENT,
  incomes: readonly Transaction[] = [],
): Decimal =>
  round2(
    transactions
      .filter(isActiveInvestment)
      .reduce<Decimal>((total, investment) => add(total, calculateCurrentValue(investment, date, annualReturnPercent, incomes)), ZERO),
  );
